import { Button, Card, Col, Empty, Form, Input, Row, Select, Space, Switch, Typography } from 'antd';
import { ValidatorRule } from 'rc-field-form/lib/interface';
import { FC, ReactElement } from 'react';
import { DashCircle, PlusCircle } from 'react-bootstrap-icons';

import { ButtonSuccess } from '@/common/components';
import { AppFormProps } from '@/common/interfaces';
import { defineInputStatus } from '@/common/utils';

import { DnsRecordFormFields, DnsRecordFormInputs } from '@/modules/DnsRecords';
import { CloudflareAccountsAllAutocomplete, DomainsApiProviders } from '@/modules/DomainsApi';


interface Props extends AppFormProps<typeof DnsRecordFormInputs, DnsRecordFormFields> {
  isEnabledDomainsApi: boolean;
  apiProviderFormValue: DomainsApiProviders | undefined;
  handleToggleDomainsApiForm: (value: boolean) => void;
}

export const DnsRecordsFormView: FC<Props> = ({
  formName,
  formType,
  form,
  initialValues,
  isFormLoading,
  formErrors,
  validationSchema,
  isEnabledDomainsApi,
  apiProviderFormValue,
  handleToggleDomainsApiForm,
  handleBack,
  handleFinish,
  handleFinishFailed
}) => {
  return (
    <Form
      form={ form }
      name={ formName }
      layout="vertical"
      initialValues={ initialValues }
      disabled={ isFormLoading }
      autoComplete="off"
      onFinish={ handleFinish }
      onFinishFailed={ handleFinishFailed }
    >
      <Form.Item
        label="Назва"
        name={ DnsRecordFormInputs.Name }
        hasFeedback
        help={ formErrors?.name }
        validateStatus={ defineInputStatus(isFormLoading, formErrors?.name) }
        rules={ validationSchema.name }
      >
        <Input allowClear/>
      </Form.Item>

      <Form.List name={ DnsRecordFormInputs.Records } rules={ validationSchema.records as ValidatorRule[] }>
        { (fields, { add, remove }, { errors }): ReactElement => (
          <Row style={{ marginBottom: 24 }}>
            <Col span={ 24 }>
              <Card
                title="Список NS адрес"
                extra={ [
                  <Button key="add" type="dashed" onClick={ (): void => add() } icon={ <PlusCircle/> }>
                    Додати NS запис
                  </Button>
                ] }
              >
                { fields.length ? (
                  fields.map((field) => (
                    <Form.Item
                      key={ field.key }
                      required={ false }
                      wrapperCol={{ span: 24 }}
                      validateTrigger={ ['onChange', 'onBlur'] }
                      validateStatus={ defineInputStatus(
                        isFormLoading,
                        form.getFieldError([DnsRecordFormInputs.Records, field.name])[0] ||
                          formErrors?.[`${ DnsRecordFormInputs.Record }[${ field.name }]`]
                      ) }
                      help={
                        form.getFieldError([DnsRecordFormInputs.Records, field.name])[0] ||
                        formErrors?.[`${ DnsRecordFormInputs.Record }[${ field.name }]`]
                      }
                    >
                      <div style={{ display: 'flex', alignItems: 'center', width: '100%', gap: 8 }}>
                        <Form.Item
                          { ...field }
                          name={ field.name }
                          rules={ validationSchema.record }
                          noStyle
                          validateTrigger={ ['onChange', 'onBlur'] }
                          validateStatus={ defineInputStatus(
                            isFormLoading,
                            formErrors?.[`${ DnsRecordFormInputs.Record }[${ field.name }]`]
                          ) }
                        >
                          <Input placeholder="ns.example.com" allowClear/>
                        </Form.Item>
                        <Button danger type="text" onClick={ (): void => remove(field.name) } icon={ <DashCircle/> }/>
                      </div>
                    </Form.Item>
                  ))
                ) : (
                  <Empty image={ Empty.PRESENTED_IMAGE_SIMPLE } description="Додайте NS запис"/>
                ) }
              </Card>
            </Col>
            <Col span={ 24 } style={{ height: errors.length || formErrors?.records ? 22 : 0 }}>
              <Form.Item>
                <Form.ErrorList errors={ formErrors?.records ? [formErrors?.records] : errors }/>
              </Form.Item>
            </Col>
          </Row>
        ) }
      </Form.List>

      <Form.Item wrapperCol={{ span: 24 }} valuePropName="checked">
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <Typography.Text>API домена</Typography.Text>
          <Switch
            checked={ isEnabledDomainsApi }
            onChange={ handleToggleDomainsApiForm }
            checkedChildren="Увімкнено"
            unCheckedChildren="Вимкнено"
          />
        </div>
      </Form.Item>

      { isEnabledDomainsApi && (
        <>
          <Form.Item
            label="API провайдер"
            name={ DnsRecordFormInputs.ApiProvider }
            hasFeedback
            help={ formErrors?.apiProvider }
            validateStatus={ defineInputStatus(isFormLoading, formErrors?.apiProvider) }
            rules={ validationSchema.apiProvider }
          >
            <Select
              allowClear
              placeholder="Виберіть API провайдера"
            >

              <Select.Option key={ DomainsApiProviders.Cloudflare } value={ DomainsApiProviders.Cloudflare }>
                <span style={{ textTransform: 'capitalize' }}>{ DomainsApiProviders.Cloudflare }</span>
              </Select.Option>
            </Select>
          </Form.Item>

          {
            apiProviderFormValue && <Form.Item
              label={ 'Акаунт Cloudflare' }
              name={ DnsRecordFormInputs.ApiProviderAccountId }
              hasFeedback
              help={ formErrors?.apiProviderAccountId }
              validateStatus={ defineInputStatus(isFormLoading, formErrors?.apiProviderAccountId) }
              rules={ validationSchema.apiProviderAccountId }
            >
              {
                apiProviderFormValue === DomainsApiProviders.Cloudflare && (
                  <CloudflareAccountsAllAutocomplete disabled={ isFormLoading || !apiProviderFormValue }/>
                )
              }
            </Form.Item>
          }
        </>
      ) }

      <Form.Item wrapperCol={{ span: 24 }}>
        <Space align="center" style={{ width: '100%', justifyContent: 'flex-end' }}>
          <Button type="default" onClick={ handleBack }>
            Скасувати
          </Button>
          <ButtonSuccess loading={ isFormLoading } type="primary" htmlType="submit">
            { formType === 'edit' ? 'Зберегти' : 'Створити' }
          </ButtonSuccess>
        </Space>
      </Form.Item>
    </Form>
  );
};
